import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Loader2, AlertTriangle, Trash2, ArrowRightLeft, Check, X } from "lucide-react";
import { toast } from "sonner";

const formatDateIt = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function OrphanRigheDialog({ open, onClose, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [items, setItems] = useState([]); // [{rapportino_id, day_idx, riga_idx, ...}]
  const [actions, setActions] = useState({}); // key -> {action, new_commessa_id}
  const [commesse, setCommesse] = useState([]);

  const rowKey = (it) =>
    `${it.rapportino_id}|${it.day_idx}|${it.riga_idx}`;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/rapportini/orphan-righe/preview"),
      api.get("/commesse"),
    ])
      .then(([resP, resC]) => {
        if (cancelled) return;
        setItems(resP.data?.orphan_righe || []);
        setCommesse(resC.data || []);
        // Default: tieni tutto
        const init = {};
        for (const it of resP.data?.orphan_righe || []) {
          init[rowKey(it)] = { action: "keep" };
        }
        setActions(init);
      })
      .catch((e) => {
        console.error("[orphan-righe] load failed:", e);
        toast.error("Caricamento righe orfane non riuscito");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const stats = useMemo(() => {
    let keep = 0, del = 0, rea = 0;
    for (const it of items) {
      const a = actions[rowKey(it)] || { action: "keep" };
      if (a.action === "delete") del++;
      else if (a.action === "reassign") rea++;
      else keep++;
    }
    return { keep, del, rea };
  }, [items, actions]);

  const setAction = (it, action, new_commessa_id = null) => {
    setActions((prev) => ({
      ...prev,
      [rowKey(it)]: { action, new_commessa_id },
    }));
  };

  const apply = async () => {
    const ops = items.map((it) => ({
      rapportino_id: it.rapportino_id,
      day_idx: it.day_idx,
      riga_idx: it.riga_idx,
      action: actions[rowKey(it)]?.action || "keep",
      new_commessa_id: actions[rowKey(it)]?.new_commessa_id || null,
    }));
    // Filtra fuori i "keep" — il backend li accetta ma è inutile inviarli
    const opsToSend = ops.filter((o) => o.action !== "keep");
    if (opsToSend.length === 0) {
      toast.info("Nessuna modifica selezionata");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/rapportini/orphan-righe/apply", {
        ops: opsToSend,
      });
      toast.success(
        `Applicato: ${data.deleted} eliminate, ${data.reassigned} riassegnate, ${data.errors?.length || 0} errori`
      );
      if (data.errors?.length) {
        console.warn("[orphan-righe] errors:", data.errors);
      }
      if (onApplied) onApplied(data);
      onClose();
    } catch (e) {
      console.error("[orphan-righe] apply failed:", e);
      toast.error("Applicazione non riuscita");
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-2 md:p-6"
      data-testid="orphan-righe-dialog"
    >
      <Card className="bg-white w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-heading font-black text-xl tracking-tight">
              Righe orfane — anteprima e correzione
            </h2>
            <p className="text-sm text-slate-600 mt-0.5">
              Queste righe hanno una commessa cancellata o un codice non
              riconosciuto. Per ognuna scegli se <b>tenere</b>, <b>riassegnare</b>
              {" "}a una commessa attiva, oppure <b>eliminare</b>. Le modifiche si
              applicano subito a tutte le stampe e ai report.
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="orphan-dialog-close"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin inline mr-2" />
              Caricamento…
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <Check className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
              Nessuna riga orfana trovata. Tutto pulito.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => {
                const k = rowKey(it);
                const a = actions[k] || { action: "keep" };
                return (
                  <div
                    key={k}
                    className={`border rounded-md p-3 text-sm grid grid-cols-1 md:grid-cols-12 gap-2 items-center ${
                      a.action === "delete"
                        ? "border-red-300 bg-red-50"
                        : a.action === "reassign"
                          ? "border-emerald-300 bg-emerald-50"
                          : "border-slate-200 bg-white"
                    }`}
                    data-testid={`orphan-row-${k}`}
                  >
                    <div className="md:col-span-3">
                      <div className="font-bold">
                        Rapportino #{it.numero || "?"}
                      </div>
                      <div className="text-xs text-slate-600">
                        {formatDateIt(it.data)}
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <div className="text-xs text-slate-500 uppercase tracking-wider">
                        Dipendente
                      </div>
                      <div className="font-medium">
                        {it.employee_nome || "—"}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-xs text-slate-500 uppercase tracking-wider">
                        Commessa (non valida)
                      </div>
                      <div className="font-mono text-amber-700 font-bold">
                        {it.commessa_codice || "—"}
                      </div>
                    </div>
                    <div className="md:col-span-1 text-right">
                      <div className="text-xs text-slate-500 uppercase tracking-wider">
                        Ore tot
                      </div>
                      <div className="font-bold tabular-nums">
                        {Number(it.ore_tot || 0).toFixed(1)}h
                      </div>
                    </div>
                    <div className="md:col-span-3 flex flex-col gap-1">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant={a.action === "keep" ? "default" : "outline"}
                          onClick={() => setAction(it, "keep")}
                          className={a.action === "keep" ? "bg-slate-600 hover:bg-slate-700 h-8" : "h-8"}
                          data-testid={`orphan-keep-${k}`}
                        >
                          Tieni
                        </Button>
                        <Button
                          size="sm"
                          variant={a.action === "reassign" ? "default" : "outline"}
                          onClick={() => setAction(it, "reassign", a.new_commessa_id)}
                          className={a.action === "reassign" ? "bg-emerald-600 hover:bg-emerald-700 h-8" : "h-8"}
                          data-testid={`orphan-reassign-${k}`}
                        >
                          <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />
                          Riassegna
                        </Button>
                        <Button
                          size="sm"
                          variant={a.action === "delete" ? "default" : "outline"}
                          onClick={() => setAction(it, "delete")}
                          className={a.action === "delete" ? "bg-red-600 hover:bg-red-700 h-8" : "h-8 text-red-600 hover:text-red-700"}
                          data-testid={`orphan-delete-${k}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {a.action === "reassign" && (
                        <Select
                          value={a.new_commessa_id || ""}
                          onValueChange={(v) =>
                            setAction(it, "reassign", v)
                          }
                        >
                          <SelectTrigger
                            className="h-8 text-xs"
                            data-testid={`orphan-commessa-select-${k}`}
                          >
                            <SelectValue placeholder="Scegli commessa..." />
                          </SelectTrigger>
                          <SelectContent>
                            {commesse.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                <span className="font-mono font-bold">{c.codice}</span>
                                {c.descrizione ? ` · ${c.descrizione}` : ""}
                                {c.cliente ? ` (${c.cliente})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex flex-col md:flex-row md:items-center gap-3">
          {items.length > 0 && (
            <div className="text-sm text-slate-700 flex-1">
              <b className="text-slate-600">{stats.keep}</b> tenute ·{" "}
              <b className="text-emerald-700">{stats.rea}</b> riassegnate ·{" "}
              <b className="text-red-700">{stats.del}</b> eliminate
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="orphan-cancel-button"
            >
              Annulla
            </Button>
            <Button
              onClick={apply}
              disabled={applying || loading || items.length === 0}
              className="bg-[#0044FF] hover:bg-[#0033CC]"
              data-testid="orphan-apply-button"
            >
              {applying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applicazione…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" /> Applica modifiche
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
